import type { VersionedDraftKit } from '@/lib/versioned-draft';
import type {
  RuleSettingsContent,
  RuleSettingsDraftContent,
  VersionedDetail,
} from '@matter_extractor/shared';
import { ruleSettingsContentSchema } from '@matter_extractor/shared';

/**
 * What Réglages > Règles holds while somebody types (vitrine.md section 5, issue
 * #192).
 *
 * The comparison that greys « Enregistrer », the version numbering and the
 * refusals of the pattern are the layer's (`versioned-draft.ts`). What is here
 * is the SHAPE of the rules' draft and the functions that cross between it and
 * the wire - `VersionedDraftKit`.
 *
 * ## The draft IS the draft content
 *
 * The two lists are one object, so the editor holds `RuleSettingsDraftContent` as
 * it is: the rules in order (the index is the priority), the guardrails in
 * reading order, each row with the identifier its version keeps. A text may be
 * EMPTY in a draft - a rule just added - and a version refuses it: the shared
 * schema is asked rather than re-decided, so the head's button and the server
 * refuse the same thing.
 */

export type RuleSettingsDraft = RuleSettingsDraftContent;

/**
 * What the tab opens on: the server draft when there is one, the `développement`
 * version otherwise.
 *
 * The singleton is born at boot, so there is always a version to open on; a
 * `null` detail is a state the screen never reaches.
 */
export function openingDraft(
  detail: VersionedDetail<RuleSettingsContent, RuleSettingsDraftContent> | null,
): RuleSettingsDraft {
  const opened = detail?.draft?.content ?? detail?.developmentContent ?? null;
  if (opened === null) {
    throw new Error('Les règles sont créées au démarrage : aucune version à ouvrir.');
  }
  return opened;
}

/**
 * The version a draft would become, or `null` while a rule is empty, a
 * guardrail misses its detection, its run of turns or its direction - the shared
 * schema decides.
 */
export function normalizedContent(draft: RuleSettingsDraft): RuleSettingsContent | null {
  const parsed = ruleSettingsContentSchema.safeParse(draft);
  return parsed.success ? parsed.data : null;
}

export const RULE_SETTINGS_DRAFT_KIT: VersionedDraftKit<
  RuleSettingsContent,
  RuleSettingsDraftContent,
  RuleSettingsDraft
> = {
  opening: openingDraft,
  fromContent: (content) => content,
  normalized: normalizedContent,
  payload: (draft) => draft,
};
